import type { ISession } from '@chainlit/react-client';

/**
 * Whether this tab still holds a session that is alive, or on its way to being
 * alive.
 *
 * A socket IS a container: connect() closes the old socket and opens a new one,
 * which force-removes the user's Docker container and starts another. So the
 * connect effect must skip a tab that already has a session — but "has a
 * session" is about liveness, not about whether a socket object happens to sit
 * in Recoil.
 *
 * socket.io defines `active` as `!!socket.subs`:
 *   - connect() calls subEvents() and sets subs synchronously, so `active` is
 *     already true during the handshake, when `connected` is still false. A
 *     guard on `connected` alone would fire a second connect() there and leave
 *     two containers behind.
 *   - the automatic reconnection attempts keep subs, so `active` stays true.
 *   - disconnect() calls destroy(), which clears subs, so `active` is false.
 *
 * clear() (New Chat) disconnects the socket but leaves the object in Recoil.
 * That is the case this predicate has to get right: the tab must reconnect.
 */
export const hasLiveSession = (session?: ISession | null): boolean =>
  !!session?.socket?.active;
